import { useCallback, useLayoutEffect, useRef, useState, type HTMLAttributes } from 'react';
import { cn } from '../../../lib/utils';

function observeResize(el: HTMLElement | null, onResize: () => void): () => void {
  if (!el || typeof ResizeObserver === 'undefined') return () => {};
  const observer = new ResizeObserver(() => onResize());
  observer.observe(el);
  return () => observer.disconnect();
}

export interface ExpandableTextProps extends Omit<HTMLAttributes<HTMLElement>, 'children'> {
  children: string;
  'data-testid'?: string;
}

/**
 * Texto de uma linha com reticências; quando não cabe, vira um botão que
 * expande o conteúdo inteiro e recolhe de volta (clique e Enter, `aria-expanded`).
 * Quando cabe, é texto puro — nenhum controle inútil para o leitor de tela.
 */
function ExpandableText({
  children,
  className,
  'data-testid': testId,
  ...props
}: ExpandableTextProps) {
  const ref = useRef<HTMLElement | null>(null);
  const [overflowing, setOverflowing] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el || expanded) return;
    setOverflowing(el.scrollWidth > el.clientWidth);
  }, [expanded]);

  useLayoutEffect(() => {
    measure();
    return observeResize(ref.current, measure);
  }, [measure, children]);

  const setRef = (el: HTMLElement | null) => {
    ref.current = el;
  };

  if (!overflowing && !expanded) {
    return (
      <span
        ref={setRef}
        data-slot="expandable-text"
        data-testid={testId}
        className={cn('block truncate', className)}
        {...props}
      >
        {children}
      </span>
    );
  }

  return (
    <button
      ref={setRef}
      type="button"
      data-slot="expandable-text"
      data-testid={testId}
      aria-expanded={expanded}
      onClick={() => setExpanded((v) => !v)}
      className={cn(
        'block w-full cursor-pointer rounded-sm text-right focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
        expanded ? 'whitespace-pre-wrap break-words text-left' : 'truncate',
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
}
ExpandableText.displayName = 'ExpandableText';

export interface OverflowListProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  items: string[];
  separator?: string;
  /** Rótulo do controle quando expandido. */
  collapseLabel?: string;
  /** Raiz do testid: texto → `<root>-visible`, controle → `<root>-more`. */
  'data-testid'?: string;
}

/**
 * Multivalor numa linha: mostra os itens que cabem e resume o excedente em
 * "+N" acionável (clique e Enter, `aria-expanded`). Com exatamente o que cabe,
 * não há "+N".
 */
function OverflowList({
  items,
  separator = ', ',
  collapseLabel = 'Mostrar menos',
  className,
  'data-testid': testId,
  ...props
}: OverflowListProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const measureRef = useRef<HTMLDivElement | null>(null);
  const [visibleCount, setVisibleCount] = useState(items.length);
  const [expanded, setExpanded] = useState(false);

  const measure = useCallback(() => {
    const container = containerRef.current;
    const measurer = measureRef.current;
    if (!container || !measurer) return;
    const available = container.clientWidth;
    const widths = Array.from(
      measurer.querySelectorAll<HTMLElement>('[data-slot="overflow-list-measure-item"]')
    ).map((el) => el.offsetWidth);
    const sep =
      measurer.querySelector<HTMLElement>('[data-slot="overflow-list-measure-separator"]')
        ?.offsetWidth ?? 0;
    const more =
      measurer.querySelector<HTMLElement>('[data-slot="overflow-list-measure-more"]')
        ?.offsetWidth ?? 0;

    const total = widths.reduce((a, b) => a + b, 0) + sep * Math.max(0, widths.length - 1);
    if (total <= available) {
      setVisibleCount(widths.length);
      return;
    }
    let fit = 1;
    for (let k = 1; k < widths.length; k += 1) {
      const used = widths.slice(0, k).reduce((a, b) => a + b, 0) + sep * (k - 1);
      if (used + sep + more <= available) fit = k;
      else break;
    }
    setVisibleCount(fit);
  }, []);

  useLayoutEffect(() => {
    measure();
    return observeResize(containerRef.current, measure);
  }, [measure, items, separator]);

  if (items.length === 0) return null;

  const hidden = items.length - visibleCount;
  const shown = expanded ? items : items.slice(0, visibleCount);

  return (
    <div
      ref={containerRef}
      data-slot="overflow-list"
      data-testid={testId}
      className={cn('relative flex min-w-0 items-baseline justify-end gap-1', className)}
      {...props}
    >
      <span
        data-testid={testId ? `${testId}-visible` : undefined}
        className={cn('min-w-0', expanded ? 'whitespace-normal break-words' : 'truncate')}
      >
        {shown.join(separator)}
      </span>
      {hidden > 0 ? (
        <button
          type="button"
          aria-expanded={expanded}
          data-testid={testId ? `${testId}-more` : undefined}
          onClick={() => setExpanded((v) => !v)}
          className="shrink-0 rounded-sm font-medium text-foreground underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          {expanded ? collapseLabel : `+${hidden}`}
        </button>
      ) : null}
      <div
        ref={measureRef}
        aria-hidden
        className="pointer-events-none invisible absolute left-0 top-0 flex whitespace-nowrap"
      >
        {items.map((item, i) => (
          <span key={`${item}-${i}`} data-slot="overflow-list-measure-item">
            {item}
          </span>
        ))}
        <span data-slot="overflow-list-measure-separator">{separator}</span>
        <span data-slot="overflow-list-measure-more">{`+${items.length}`}</span>
      </div>
    </div>
  );
}
OverflowList.displayName = 'OverflowList';

export { ExpandableText, OverflowList };
